import {
  WhmAutocomplete,
  WhmBox,
  WhmButton,
  WhmFade,
  WhmStack,
  WhmTextField,
  WhmTypography,
} from "@securitize/reactjs-whm";
import secLogoPositive from "@securitize/assets-sec-ui/dist/logos/svg/sec-positive.svg";
import { useFormikContext } from "formik";
import { statesUS } from "./countriesList";
import TranslationKeys from "../../contexts/I18nContext/translationKeys";
import { Translate } from "../Translation/Translate";
import { FormType } from "./types";
import React from "react";
import CountryTextField from "../shared/CountryTextField/CountryTextField";
import { PulsatingSpinner } from "../shared/PulsatingSpinner";
import { ErrorPage } from "../shared/ErrorPage";
import { useCountries } from "../shared/CountryTextField/CountriesContext";

export default function EntityInfoStep({
  resizeOnChange,
  filler,
}: {
  resizeOnChange?: () => void;
  filler?: React.ReactNode;
}) {
  const formik = useFormikContext<FormType>();
  const countriesQuery = useCountries();

  // Show error state if countries failed to load
  if (countriesQuery.error) {
    return (
      <WhmStack sx={{ minHeight: "100%" }} spacing={6} alignItems="center">
        <ErrorPage
          errorCode={countriesQuery.error.correlationId}
          onRefresh={countriesQuery.refetch}
        />
      </WhmStack>
    );
  }

  return countriesQuery.isLoading ? (
    <WhmBox sx={{ display: "flex", justifyContent: "center", my: 2 }}>
      <PulsatingSpinner />
    </WhmBox>
  ) : (
    <WhmStack sx={{ minHeight: "100%" }} spacing={6} alignItems="center">
      <WhmBox
        component="img"
        sx={{ height: "24px" }}
        src={secLogoPositive}
        alt="Securitize Logo"
      />
      <WhmStack alignItems="center" sx={{ marginBottom: "8px !important" }}>
        <WhmTypography variant="h4" align="center">
          <Translate
            translationKey={TranslationKeys.REGISTRATION_STEP_ENTITY_TITLE}
          />
        </WhmTypography>
      </WhmStack>
      <WhmStack spacing={4} alignItems="center" sx={{ width: "100%" }}>
        <WhmTextField
          label={
            <Translate
              translationKey={TranslationKeys.REGISTRATION_ENTITY_NAME}
            />
          }
          fullWidth
          name="entityName"
          value={formik.values.entityName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.entityName && Boolean(formik.errors.entityName)}
          helperText={formik.touched.entityName && formik.errors.entityName}
        />
        <CountryTextField
          name="entityCountry"
          value={formik.values.entityCountry?.value ?? ""}
          label={
            <Translate
              translationKey={TranslationKeys.REGISTRATION_ENTITY_COUNTRY}
            />
          }
          fieldError={
            formik.touched.entityCountry && Boolean(formik.errors.entityCountry)
          }
          helperText={
            formik.touched.entityCountry && formik.errors.entityCountry
              ? String(formik.errors.entityCountry)
              : undefined
          }
          onChange={(e, v) => {
            void formik.setFieldValue("entityCountry", v);
          }}
          onBlur={() => {
            void formik.setFieldTouched("entityCountry");
          }}
        />
        <WhmFade
          in={formik.values.entityCountry?.value === "US"}
          mountOnEnter
          unmountOnExit
          onEnter={resizeOnChange}
          onExited={() => {
            resizeOnChange && requestAnimationFrame(resizeOnChange);
          }}
        >
          <WhmAutocomplete
            options={statesUS}
            onBlur={() => void formik.setFieldTouched("entityState")}
            onChange={(e, v) => void formik.setFieldValue("entityState", v)}
            value={formik.values.entityState}
            fullWidth
            renderInput={(props) => (
              <WhmTextField
                {...props}
                label={
                  <Translate
                    translationKey={TranslationKeys.REGISTRATION_ENTITY_STATE}
                  />
                }
                name="state"
                error={
                  formik.touched.entityState &&
                  Boolean(formik.errors.entityState)
                }
                helperText={
                  formik.touched.entityState && formik.errors.entityState
                }
              />
            )}
          />
        </WhmFade>
      </WhmStack>
      {filler}
      <WhmButton variant="contained" type="submit" size="large" fullWidth>
        <Translate translationKey={TranslationKeys.REGISTRATION_NEXT} />
      </WhmButton>
    </WhmStack>
  );
}
